/**
 * La parte PURA de conectar un monedero: traducir la prueba y nada mas.
 *
 * Esta separada de `monedero.ts` por un motivo practico: `monedero.ts` arrastra
 * `api.ts`, que al importarse ya mira si hay Telegram en la ventana. Eso lo
 * hace imposible de probar sin montar un navegador, y **esto es justo lo que
 * hay que probar**: es el sitio donde un nombre mal puesto no da error, da
 * «firma invalida» para todo el mundo, y entonces nadie cobra y nadie sabe por
 * que.
 *
 * Aqui no se importa nada. Se puede probar en un test de verdad.
 */

/** El cuerpo exacto que espera `POST /api/wallet/verificar`. */
export interface CuerpoVerificar {
  direccion: string;
  dominio: string;
  timestamp: number;
  payload: string;
  firma: string;
}

/**
 * La forma de lo que devuelve TON Connect al conectar, en lo que nos importa.
 *
 * Se declara a mano y no se importa del SDK **a proposito**: si se importara el
 * tipo del paquete, este fichero dependeria de el y el `import()` perezoso de
 * `monedero.ts` dejaria de ser lo unico que lo trae. Un tipo se borra al
 * compilar, pero el import se queda.
 */
export interface MonederoConectado {
  account?: { address?: string };
  connectItems?: {
    tonProof?: {
      proof?: {
        timestamp?: number;
        domain?: { value?: string };
        signature?: string;
        payload?: string;
      };
      error?: { message?: string };
    };
  };
}

/** Lo que falla cuando el monedero contesta algo con lo que no se puede ir al servidor. */
export class PruebaIncompleta extends Error {}

/**
 * Los puentes que la CSP deja usar, y por tanto los unicos monederos que se
 * ofrecen.
 *
 * **Tiene que decir lo mismo que el `connect-src` de
 * `infra/cabeceras-seguridad.conf`.** Hay un test que compara los dos
 * ficheros: si alguien anade un monedero aqui y se olvida de nginx, el jugador
 * toca «Conectar», la conexion se corta y **no se ve ni un error**, que es como
 * la CSP tumbo el personaje 3D (D-027).
 *
 *   walletbot.me      el monedero de Telegram, el que la mayoria ya tiene
 *   bridge.tonapi.io  Tonkeeper, el mas usado fuera de Telegram
 */
export const PUENTES_PERMITIDOS: readonly string[] = [
  'https://walletbot.me',
  'https://bridge.tonapi.io',
];

/**
 * La lista de monederos de PRODUCCION.
 *
 * Leida del propio SDK (`dist/tonconnect-sdk.min.js`) y no de la memoria de
 * nadie: la que anda por ahi en `raw.githubusercontent.com` es la de STAGING.
 */
export const LISTA_DE_MONEDEROS = 'https://config.ton.org/wallets-v2.json';

/**
 * Traduce lo que dio el monedero al cuerpo que espera el servidor.
 *
 * El servidor ya sabe comprobarla: `app/services/ton.py` lleva el formato de
 * `ton_proof` escrito entero, leido de `docs.ton.org`, con las dos endianness
 * distintas que la especificacion pide en el mismo mensaje. **Aqui no se
 * reinventa nada de eso**: solo se cambian los nombres.
 *
 *     account.address                  -> direccion   (cruda, `0:...`)
 *     connectItems.tonProof.proof
 *       .domain.value                  -> dominio
 *       .timestamp                     -> timestamp
 *       .payload                       -> payload
 *       .signature                     -> firma       (base64)
 *
 * Y se comprueba que esten TODAS antes de salir: mandar al servidor una prueba
 * a medias gasta un intento del limite —diez cada cinco minutos— y devuelve un
 * 400 que no explica nada.
 */
export function cuerpoDeVerificacion(monedero: MonederoConectado): CuerpoVerificar {
  const prueba = monedero.connectItems?.tonProof;
  if (!prueba) {
    throw new PruebaIncompleta('el monedero no devolvio ninguna prueba');
  }
  if (prueba.error) {
    throw new PruebaIncompleta(prueba.error.message || 'el monedero rechazo firmar');
  }

  const direccion = monedero.account?.address;
  const p = prueba.proof;
  const dominio = p?.domain?.value;
  const firma = p?.signature;
  const payload = p?.payload;
  const timestamp = p?.timestamp;

  // Se nombra lo que falta: si un dia el SDK cambia un nombre, el mensaje dice
  // cual, en vez de dejar un «algo salio mal».
  const falta = [
    ['direccion', direccion],
    ['dominio', dominio],
    ['firma', firma],
    ['payload', payload],
    ['timestamp', timestamp],
  ]
    .filter(([, v]) => v === undefined || v === null || v === '')
    .map(([n]) => n);
  if (falta.length > 0) {
    throw new PruebaIncompleta(`la prueba del monedero no trae: ${falta.join(', ')}`);
  }

  return {
    direccion: direccion as string,
    dominio: dominio as string,
    timestamp: timestamp as number,
    payload: payload as string,
    firma: firma as string,
  };
}
